import { useState } from 'react'
import { AlertCircle, Send } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { usePublishMenu } from '../hooks/use-menu-mutations'
import type { Menu, PublishMenuResponse } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { ApiError } from '@/api/types'

interface PublishMenuButtonProps {
  menu: Menu
}

export function PublishMenuButton({ menu }: PublishMenuButtonProps) {
  const { t: tMenus } = useTranslation('menus')
  const [showConfirm, setShowConfirm] = useState(false)
  const [missingPrices, setMissingPrices] = useState<Array<string>>([])
  const publishMenu = usePublishMenu()

  const handlePublish = async () => {
    try {
      await publishMenu.mutateAsync(menu.id)
      setShowConfirm(false)
      setMissingPrices([])
    } catch (error) {
      // Check if it's a 409 with missing prices
      if (error instanceof ApiError && error.status === 409) {
        const responseData = error.data as PublishMenuResponse
        if (responseData.items) {
          // Extract SKUs from items array and remove duplicates
          const skus = [...new Set(responseData.items.map((item) => item.sku))]
          setMissingPrices(skus)
          // Keep dialog open to show errors
          return
        }
      }
      // For other errors, close the dialog
      setShowConfirm(false)
    }
  }

  return (
    <>
      <Button size="sm" onClick={() => setShowConfirm(true)}>
        <Send className="w-4 h-4 mr-2" />
        {tMenus('publish.title')}
      </Button>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tMenus('publish.title')}</DialogTitle>
            <DialogDescription>
              {tMenus('publish.description')}
            </DialogDescription>
          </DialogHeader>

          {missingPrices.length > 0 ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive mb-2">
                      {tMenus('publish.missingTitle')}
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      {tMenus('publish.missingListIntro')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {missingPrices.map((sku) => (
                        <Badge key={sku} variant="destructive">
                          {sku}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                {tMenus('publish.missingHint')}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border p-4 bg-muted/50">
              <p className="text-sm text-muted-foreground">
                {tMenus('publish.confirm', {
                  name: menu.name,
                })}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowConfirm(false)
                setMissingPrices([])
              }}
              disabled={publishMenu.isPending}
            >
              {tMenus('publish.cancel')}
            </Button>
            {missingPrices.length === 0 && (
              <Button onClick={handlePublish} disabled={publishMenu.isPending}>
                {publishMenu.isPending
                  ? tMenus('publish.publishing')
                  : tMenus('publish.publish')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
